import { Injectable, TemplateRef } from '@angular/core';
import { ModalButtonOptions, NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationRef, NzNotificationService } from 'ng-zorro-antd/notification';
import { ModalContentComponent, resultType } from './modal-content/modal-content.component';
import { IncomingCallModalComponent } from './incoming-call-modal/incoming-call-modal.component';
import { PhoneCallCustomer } from '../models/call';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  private quoteActionTemplate: TemplateRef<{}>;

  constructor(private notification: NzNotificationService, private modal: NzModalService, private router: Router) { }

  public showError(message: string, title? : string): void {
    title = title == undefined ? 'Error' : title;
    this.notification.error(title, message);
  }

  public showWarning(message: string): void {
    this.notification.warning('Warning', message);
  }

  public showWarningTimer(message: string, timer: number): void {
    this.notification.warning('Warning', message, {nzDuration: timer});
  }

  public showSuccess(message: string): void {
    this.notification.success('Success', message);
  }

  public removeAlert(): void {
    this.notification.remove();
    this.modal.closeAll();
  }

  public showSuccessMinimal(message: string): void {
    this.notification.success("",message,{nzDuration: 1000});
  }

  public showSuccessPinned(message: string): void {
    this.notification.success('Success', message, {nzDuration: 0});
  }

  public showInfo(message: string): void {
    this.notification.info('Info', message);
  }

  public handleError(error: any, message? : string, title?: string): void {
    const msg = error ? error.error ? error.error.field ? error.error.field + " " + error.error.message : error.error.detail ? error.error.detail : error.statusText : "Unknown error" : "Unknown error";
    console.error(error);
    this.showError(message == undefined ? msg : message + " :: " + msg, title);
  }

  public handleWarning(error: any, message? : string): void {
    const msg = error ? error.error ? error.error.field ? error.error.field + " " + error.error.message : error.error.detail ? error.error.detail : error.statusText : "Unknown error" : "Unknown error";
    this.showWarning(message == undefined ? msg : message + " :: " + msg);
  }

  public handleErrorSilent(error: any, message? : string, title?: string): void {
    const msg = error ? error.error ? error.error.field ? error.error.field + " " + error.error.message : error.error.detail ? error.error.detail : error.statusText : "Unknown error" : "Unknown error";
    console.error(error);
  }

  public ShowBlockingPopup(message: string, title? : string, width?: string | number) { 
    this.ShowAlert({type: resultType.Information, title: title ? title : "NOTICE", htmlMessage: message, btnText: "OK", maskClosable: false}, width)
  }

  
  public ShowAlert(opts: AlertModalOptions, width? : string | number, customOnClick?: () => void) {
    let footerBtns: Array<ModalButtonOptions<ModalContentComponent>> = [];
    if (opts.btnText) {
      footerBtns = [{
        label: opts.btnText,
        type: "primary",
        // Adding ths to handle onclick events 
        onClick: (contentInstance: ModalContentComponent)  => {
          if (customOnClick) {
            customOnClick();
          }
          this.modal.closeAll();
        }        
      }]
    }
    let ref = this.modal.create({
      nzTitle: "",
      nzContent: ModalContentComponent,
      nzFooter: footerBtns.length > 0 ? footerBtns : null,
      nzMaskClosable: opts.maskClosable === undefined ? true : opts.maskClosable,
      nzClosable: opts.closable === undefined ? true : opts.closable,
      nzWidth: width ? width : 600,
      nzData:{
        title: opts.title,
        message: opts.message,
        htmlMessage: opts.htmlMessage,
        notiType: opts.type
      }
    });
    if (opts.autoCloseInMilliseconds) {
      setTimeout(() => ref.destroy(), opts.autoCloseInMilliseconds);
      
    }
    return ref;
  }

  public ShowNonBlockingMessage(message: string) {
    this.notification.info('NOTICE', message, {nzDuration:0, nzClass: 'ws-noti'});
  }

  public showIncomingCallNotification(call: PhoneCallCustomer, msg: string): NzNotificationRef {
    const notificationRef = this.notification.create(
      'info',
      msg,
      `${call.name} - ${call.phoneNumber} (Last Salesman: ${call.lastUser || 'N/A'})`,
      {
        nzDuration: 60000, // 1 minute duration
        nzClass: 'clickable-notification incoming-call-notification',
        nzStyle: { cursor: 'pointer' },
        nzData: { call }
      }
    );

    notificationRef.onClick.subscribe(() => {
      this.modal.create({
        nzTitle: msg,
        nzContent: IncomingCallModalComponent,
        nzFooter: null,
        nzMaskClosable: false,
        nzClosable: false,
        nzWidth: 400,
        nzData: { call }
      });
      this.dismissIncomingCallNotification(notificationRef);
    });

    return notificationRef;
  }

  public dismissIncomingCallNotification(notificationRef: NzNotificationRef): void {
    this.notification.remove(notificationRef.messageId);
  }

  public registerQuoteActionTemplate(template: TemplateRef<{}>): void {
    this.quoteActionTemplate = template;
  }

  public showQuoteActionNotification(
    message: string,
    quoteIdentifier: string,
    quoteUrl: string,
    onOpenQuote: () => void
  ): NzNotificationRef {
    if (!this.quoteActionTemplate) {
      throw new Error('Quote action notification template has not been registered');
    }
    const data = {
      message,
      quoteIdentifier,
      quoteUrl,
      opened: false,
      openQuote: (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (data.opened || !quoteUrl) {
          return;
        }
        data.opened = true;
        try {
          // Dispatch cancellation before removal completes the notification's events.
          onOpenQuote();
          this.router.navigateByUrl(quoteUrl);
        } finally {
          this.dismissQuoteActionNotification(notificationRef);
        }
      }
    };
    const notificationRef = this.notification.template(this.quoteActionTemplate, {
      nzDuration: message === 'Quote action received: Call me' ? 0 : 10000,
      nzClass: 'quote-action-notification',
      nzStyle: { cursor: 'default' },
      nzData: data
    });

    return notificationRef;
  }

  public dismissQuoteActionNotification(notificationRef: NzNotificationRef): void {
    this.notification.remove(notificationRef.messageId);
  }

  //this is a custom notification that can be clicked to open the message on the messages page
  public showClickableMessageNotification(title: string, message: string, messageUuid: string): void {
    const notificationRef = this.notification.create(
      'info',
      title,
    message,
    {
        nzDuration: 0,
        nzClass: 'clickable-notification',
        nzStyle: { cursor: 'pointer' },
        nzData: { messageUuid: messageUuid }
      }
  );

  setTimeout(() => {
    const notificationElement = document.querySelector('.clickable-notification');
    if (notificationElement) {
      notificationElement.addEventListener('click', () => {
        this.router.navigate(['/messages', messageUuid]);
        this.notification.remove(notificationRef.messageId);
      });
    }
  },100)
  }
    //this is a custom notification that can be clicked to open the message on the messages page
  public showClickableNotification(title: string, message: string, url: string): void {
    const notificationRef = this.notification.create(
      'info',
      title,
      message,
      {
        nzDuration: 0,
        nzClass: 'clickable-notification',
        nzData: { url }
      }
    );

    setTimeout(() => {
      const notificationElement = document.querySelector(
        `.clickable-notification`
      );

      if (notificationElement) {
        const closeButton = notificationElement.querySelector(
          '.ant-notification-notice-close'
        );

        const clickableContent = notificationElement.querySelector(
          '.ant-notification-notice-content'
        );

        if (clickableContent) {
          clickableContent.setAttribute('style', 'cursor: pointer');

          clickableContent.addEventListener('click', () => {
            sessionStorage.setItem('isFleetAuthModalVisible', 'true');
            this.router.navigate([url]);
            this.notification.remove(notificationRef.messageId);
          });
        }

        // Prevent the close button click from triggering the notification click
        closeButton?.addEventListener('click', (event) => {
          event.stopPropagation();
        });
      }
    }, 100);
  }
}


export interface AlertModalOptions {
  type: resultType,
  title?: string, 
  closable?: boolean, 
  maskClosable?: boolean, 
  btnText?: string, 
  message? : string, 
  htmlMessage? :string, 
  autoCloseInMilliseconds? : number
}

